import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, Users } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";

export default function AdminUsers() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api
      .get("/admin/users")
      .then((res) => setData(res.data))
      .catch(() => setError(t.dashboard.adminError));
  }, [t.dashboard.adminError]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/users/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `use1id-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(t.dashboard.adminError);
    } finally {
      setExporting(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="admin-users-panel"
      className="mb-16 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-u-purple/12 text-u-purple">
            <Users size={18} />
          </span>
          <div>
            <h2 className="font-display text-xl md:text-2xl font-semibold text-u-ink">
              {t.dashboard.adminTitle}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">{t.dashboard.adminSubtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {data && (
            <span
              data-testid="admin-users-count"
              className="text-sm rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-slate-600"
            >
              {t.dashboard.adminTotal}: <strong className="text-u-ink">{data.count}</strong>
            </span>
          )}
          <button
            onClick={handleExport}
            disabled={exporting || !data}
            data-testid="admin-export-csv"
            className="inline-flex items-center gap-2 rounded-full bg-u-ink px-4 py-2 text-sm font-medium text-white hover:bg-u-ink/90 transition-colors disabled:opacity-50"
          >
            <Download size={15} />
            {exporting ? t.dashboard.adminExporting : t.dashboard.adminExport}
          </button>
        </div>
      </div>

      {error && (
        <p data-testid="admin-users-error" className="text-sm text-red-600">
          {error}
        </p>
      )}

      {data && (
        <div className="overflow-x-auto rounded-xl border border-slate-100">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left font-medium px-4 py-2.5">#</th>
                <th className="text-left font-medium px-4 py-2.5">Naam</th>
                <th className="text-left font-medium px-4 py-2.5">E-mail</th>
                <th className="text-left font-medium px-4 py-2.5">Rol</th>
                <th className="text-left font-medium px-4 py-2.5">Datum</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u, i) => (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 text-slate-400">{i + 1}</td>
                  <td className="px-4 py-2.5 text-u-ink font-medium">{u.name || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${u.role === "admin" ? "bg-u-purple/12 text-u-purple" : "bg-slate-100 text-slate-500"}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.section>
  );
}
